<template>
  <div
    :id="uid"
    class="d-swiperRoot"
    :class="[{ 'd-swiperRoot-pagenation': pagenation }, {'d-swiperRoot-noSwiper': !isEnableSwiper}]"
    :style="swiperOverrideStyle"
  >
    <div
      class="d-swiper"
      :class="{'d-swiper-noSwiper': !isEnableSwiper}"
      ref="swiper"
    >
      <div class="d-swiper_wrapper swiper-wrapper">
        <slot />
      </div>
    </div>
    <div v-show="isEnableSwiper && !!swiper">
      <div class="d-swiper_pagination" v-if="pagenation"/>
      <button
        v-if="prevBtn"
        class="d-swiper_button d-swiper_button-prev"
        :class="{ 'd-swiper_button-pagenation': pagenation }"
      >
        <CoconalaIcon
          class="d-swiper_buttonIcon"
          name="chevron-left"
          size="12px"
          color="gray800"
        />
      </button>
      <button
        v-if="nextBtn"
        class="d-swiper_button d-swiper_button-next"
        :class="{ 'd-swiper_button-pagenation': pagenation }"
      >
        <CoconalaIcon
          class="d-swiper_button_icon"
          name="chevron-right"
          size="12px"
          color="gray800"
        />
      </button>
    </div>
  </div>
</template>
<script>
export default {
  name: 'DSwiper',
  props: {
    isEnableSwiper: {
      type: Boolean,
      default: true
    },
    itemViewCount: {
      type: Number,
      required: true
    },
    itemStyle: {
      type: Object,
      required: true,
      validator: obj => {
        const property = ['width', 'marginRight']
        return property.every(p => obj.hasOwnProperty(p))
      }
    },
    prevBtn: {
      type: Boolean,
      default: true
    },
    nextBtn: {
      type: Boolean,
      default: true
    },
    pagenation: {
      type: Boolean,
      default: true
    },
    // 動的に自動スクロールを開始・停止したい場合に使用
    autoplay: {
      type: Boolean,
      default: false
    },
    swiperOption: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      swiper: null,
      swiperModule: null
    }
  },
  computed: {
    // 同一ページで複数のDSwiperを使用しても影響のないようにuidを使用
    uid() {
      // ランダムな文字列生成
      const ramdom = Math.random()
        .toString(32) // 英数字に変換
        .substring(2) // 小数点以下のみ取得
      return `swiper-${ramdom}`
    },
    option() {
      const defaultOption = {
        // 一番最初に表示させるスライドの番号(default: 0)
        initialSlide: 0,
        // スライドの速度。数字が大きければ遅く、小さければ早くなる(default: 300)
        speed: 300,
        loop: true,
        // 「slidesPerGroup」が設定されており、ページ送りした時にスライドの枚数が場合、あまり部分を空白のままか否か
        loopFillGroupWithBlank: true,
        // １画面で見えるスライドの数
        slidesPerView: this.itemViewCount,
        // 複数のスライドを１つにまとめる。スワイプの際に同時にスワイプされる数
        slidesPerGroup: 1,
        // スライドショーの自動開始
        autoplay: false,
        // true => スライドのスタート(デフォルトでは0枚目)が左端じゃなくて真ん中になる。
        centeredSlides: false,
        // スライド間のスペース
        spaceBetween: this.itemStyle.marginRight,
        pagination: {
          el: `#${this.uid} .d-swiper_pagination`,
          clickable: true
        },
        navigation: {
          nextEl: `#${this.uid} .d-swiper_button-next`,
          prevEl: `#${this.uid} .d-swiper_button-prev`
        },
        // SP対応するなら使用
        breakpoints: {}
      }
      return {
        ...defaultOption,
        ...this.swiperOption
      }
    },
    swiperOverrideStyle() {
      return {
        '--slideWidth': `${this.itemViewCount * this.itemStyle.width +
          (this.itemViewCount - 1) * this.itemStyle.marginRight}px`
      }
    }
  },
  watch: {
    isEnableSwiper(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.initSwiper()
        })
      } else {
        this.swiper?.destroy()
        this.swiper = null
      }
    },
    autoplay(newVal) {
      if (!this.swiper) {
        return
      }
      if (newVal) {
        this.swiper.autoplay.start()
      } else {
        this.swiper.autoplay.stop()
      }
    }
  },
  mounted() {
    if (!this.isEnableSwiper) {
      return
    }
    this.$nextTick(() => {
      this.initSwiper()
    })
  },
  beforeDestroy() {
    // swiper初期化前に遷移するとエラーになるのでOptional Chainingにする
    this.swiper?.destroy()
  },
  methods: {
    async initSwiper() {
      if (!this.swiperModule) {
        this.swiperModule = (await import('swiper/dist/js/swiper.esm.bundle')).default
      }
      this.swiper = new this.swiperModule(this.$refs.swiper, this.option)
    }
  }
}
</script>
<style lang="scss" scoped>
$slide-arrow-size: 32px;
$swiper-pagenation-space: 24px;

.d-swiperRoot {
  width: var(--slideWidth);
  height: 100%;
  position: relative;
  margin: auto;

  &-pagenation {
    // pagenationを表示するために使用
    padding-bottom: $swiper-pagenation-space;
  }
  &-noSwiper {
    width: auto;
  }
}

.d-swiper {
  height: 100%;
  overflow: hidden;
  position: relative;

  &-noSwiper {
    width: unset;
    overflow: auto;
  }

  &_wrapper {
    display: flex;
    position: absolute;
    top: 0;
    left: 0;
  }

  &_pagination {
    display: flex;
    justify-content: center;
    position: absolute;
    bottom: 0;
    width: 100%;
    /deep/ .swiper-pagination-bullet {
      width: 6px;
      height: 6px;
      margin: 0 2px;
      background-color: $color-gray-300;
      opacity: 1;
      &-active {
        background-color: $color-gray-700;
      }
    }
  }
  &_button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    justify-content: center;
    align-items: center;
    width: $slide-arrow-size;
    height: $slide-arrow-size;
    border-radius: 50%;
    background-color: $color-white;
    box-shadow: 0 2px 6px rgba(2, 16, 66, 0.1);
    padding: 0;
    z-index: 15;
    cursor: pointer;
    border: none;
    margin-top: (-$slide-arrow-size / 4);
    &-prev {
      left: (-$slide-arrow-size / 2);
    }
    &-next {
      right: (-$slide-arrow-size / 2);
    }
    &_icon {
      height: 12px;
    }
    &-pagenation {
      margin-top: ($slide-arrow-size / 4) - $swiper-pagenation-space;
    }
  }
}
</style>
